import type { MouseEventHandler } from 'react';
import React from 'react';
import { forwardRef } from '@shared/utils/forwardRef';
import { UseSharedCatalogMutationReturn } from '../../../../Catalog.controller';
import { CatalogProductTileItem, MinimalCatalogProductItemDetails } from '../../../../Catalog.types';
import { animationTransitionExt } from '@shared/utils/animationTransition';
import { useCatalogContext } from '../../../../state/Catalog';
import { BuyForm } from '../../../ProductDetails/components/BuyForm';
import { useQuickAddForm } from './QuickAddForm.controller';
import { QuickAddFormProps } from './QuickAddForm.types';
import { StyledQuickAddFormBox } from './QuickAddForm.styles';
import { Box, BoxProps } from '@withjoy/joykit';
import { EditCashFundCta } from '../EditCashFundCta';
import { isCashOrCharityCatalogItem } from '../../../../utils/interpretItemType';
import { GetCatalogProductDetailsQuery } from '@graphql/generated';

export interface QuickAddFormRootProps extends BoxProps {
  addCatalogItem: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  isOpen: boolean;
  showQuantity: boolean;
  onViewDetailsClick: MouseEventHandler;
  selectedProduct: Maybe<CatalogProductTileItem>;
  setProductSelected?: (product: MinimalCatalogProductItemDetails) => void;
  offset?: string;
  productId: string;
  product?: GetCatalogProductDetailsQuery['productCatalogItemById'];
  onEditAndAddCashFund: React.MouseEventHandler;
  itemPosition: number;
}

export const QuickAddForm = forwardRef<'div', QuickAddFormProps>((props, ref) => {
  const { isOpen, productId, readyProduct, ...restProps } = props;

  if (!isOpen || !productId) {
    return null;
  }

  return readyProduct ? (
    <QuickAddFormRoot productId={productId} isOpen={isOpen} product={readyProduct} {...restProps} />
  ) : (
    <LoadingQuickAddForm productId={productId} isOpen={isOpen} {...restProps} />
  );
});

export const LoadingQuickAddForm = forwardRef<'div', QuickAddFormRootProps>(props => {
  const { product } = useQuickAddForm({ catalogItemId: props.selectedProduct?.id || props.productId });
  return <QuickAddFormRoot product={product} {...props} />;
});

export const QuickAddFormRoot = forwardRef<'div', QuickAddFormRootProps>((props, ref) => {
  const { onViewDetailsClick, addCatalogItem, isOpen, offset, setProductSelected, selectedProduct, showQuantity, product, onEditAndAddCashFund, ...restProps } = props;
  const { isMobile, addCatalogItemLoading } = useCatalogContext();
  const mode = 'compact';

  const contentMarkup =
    product &&
    (isCashOrCharityCatalogItem(product) ? (
      <EditCashFundCta onClick={onEditAndAddCashFund} />
    ) : (
      <BuyForm
        isMobile={isMobile}
        showQuantity={showQuantity}
        isQuickAddForm
        isAddingItem={addCatalogItemLoading}
        showAllDimensions={true}
        showBuyButton={false}
        relatedItems={product?.group?.items}
        onViewDetailsClick={onViewDetailsClick}
        product={product}
        addCatalogItem={addCatalogItem}
        setProductSelected={setProductSelected}
        selectedProduct={selectedProduct}
        dimensionSpacing={5}
        colorSwatchVariant="carousel"
        itemPosition={props.itemPosition}
      />
    ));

  return !isMobile ? (
    <StyledQuickAddFormBox
      alignItems="flex-start"
      zIndex={1}
      backgroundColor={'white'}
      transition={animationTransitionExt(
        {
          property: 'top',
          delay: isOpen ? '100ms' : undefined
        },
        { property: 'background-color' }
      )}
      ref={ref}
      {...restProps}
    >
      <Box paddingX={[3, null, 0]}>
        {!product && <BuyForm.Skeleton mode={mode} isQuickAddForm={true} />}
        {contentMarkup}
      </Box>
    </StyledQuickAddFormBox>
  ) : isOpen ? (
    <Box ref={ref} {...restProps}>
      {!product && <BuyForm.Skeleton mode={mode} />}
      {contentMarkup}
    </Box>
  ) : null;
});
